"use client";

import { Button } from "@/components/Button/Button";
import useCartStore from "@/store/useCartStore";
import { AddToCartTicketType } from "@/types/CartType";
import { ProductType } from "@/types/TicketType";
import { getGeneralTicketId } from "@/utils/ticketUtils";
import { ReactNode, useState } from "react";
import { CalendarTicketPopUp } from "../../CalendarTicketPopUp/CalendarTicketPopUp";
import styles from './AddtoCartForm.module.scss';
import { DropDownWithPrice } from "./DropDownWithPrice/DropDownWithPrice";
import { YearPicker } from "./YearPicker/YearPicker";

type AddtoCartProps = {
  ticket: ProductType;
  name: String;
}

export function AddtoCartForm({ ticket, name }: AddtoCartProps) {

  const { addToCart } = useCartStore((state) => state);

  const [loading, setLoading] = useState<boolean | undefined>(false)
  const [adultQty, setAdultQty] = useState<number>(0)
  const [childQty, setChildQty] = useState<number>(0)
  const [infantQty, setInfantQty] = useState<number>(0)
  const [generalQty, setGeneralQty] = useState<number>(0)
  const [year, setYear] = useState<string>(new Date().getFullYear().toString())

  const generalTicketId = getGeneralTicketId(ticket);
  const adultTicketId = ticket.seasons.find(s => s.title === year)?.tickets.find(t => t.type === "Adult")?.ticket_id
  const childTicketId = ticket.seasons.find(s => s.title === year)?.tickets.find(t => t.type === "Child")?.ticket_id
  const parkExtarsAdultId = !ticket.seasons.length ? ticket.tickets.find(t => t.type === "Adult")?.ticket_id : null
  const parkExtarsChildId = !ticket.seasons.length ? ticket.tickets.find(t => t.type === "Child")?.ticket_id : null
  const parkExtrasInfantId = !ticket.seasons.length ? ticket.tickets.find(t => t.type === "Infant")?.ticket_id : null

  const generalTicket = ticket.tickets.find(t => t.ticket_id === generalTicketId)
  const adultTicket = ticket.tickets.find(t => t.ticket_id === adultTicketId)
  const childTicket = ticket.tickets.find(t => t.ticket_id === childTicketId)
  const parkExtrasAdultTicket = ticket.tickets.find(t => t.ticket_id === parkExtarsAdultId)
  const parkExtrasChildTicket = ticket.tickets.find(t => t.ticket_id === parkExtarsChildId)
  const parkExtrasInfantTicket = ticket.tickets.find(t => t.ticket_id === parkExtrasInfantId)

  const adultPrice = ticket.seasons.find(s => s.title === year)?.tickets.find(t => t.ticket_id === adultTicket?.ticket_id)?.price ?? ''
  const childPrice = ticket.seasons.find(s => s.title === year)?.tickets.find(t => t.ticket_id === childTicket?.ticket_id)?.price ?? ''
  const parkExtrasAdultPrice = ticket.tickets.find(t => t.ticket_id === parkExtarsAdultId)?.price_from ?? ''
  const parkExtrasChildPrice = ticket.tickets.find(t => t.ticket_id === parkExtarsChildId)?.price_from ?? ''
  const parkExtrasInfantPrice = ticket.tickets.find(t => t.ticket_id === parkExtrasInfantId)?.price_from ?? ''

  const generalDropDown = generalTicket ? <DropDownWithPrice ticketPrice={generalTicket.price_from} type={'General'} setQty={(qty: number) => setGeneralQty(qty)} qty={generalQty} /> : null
  const adultDropDown = adultTicket ? <DropDownWithPrice ticketPrice={adultPrice} type={adultTicket.type} setQty={(qty: number) => setAdultQty(qty)} qty={adultQty} /> : null
  const childDropDown = childTicket ? <DropDownWithPrice ticketPrice={childPrice} type={childTicket.type} setQty={(qty: number) => setChildQty(qty)} qty={childQty} /> : null
  const parkExtrasAdultDropDown = parkExtrasAdultTicket ? <DropDownWithPrice ticketPrice={parkExtrasAdultPrice} type={parkExtrasAdultTicket.type} setQty={(qty: number) => setAdultQty(qty)} qty={adultQty} /> : null
  const parkExtrasChildDropDown = parkExtrasChildTicket ? <DropDownWithPrice ticketPrice={parkExtrasChildPrice} type={parkExtrasChildTicket.type} setQty={(qty: number) => setChildQty(qty)} qty={childQty} /> : null
  const parkExtrasInfantDropDown = parkExtrasInfantTicket ? <DropDownWithPrice ticketPrice={parkExtrasInfantPrice} type={parkExtrasInfantTicket.type} setQty={(qty: number) => setInfantQty(qty)} qty={infantQty} /> : null

  const availableYears = Array.from(new Set(ticket.seasons.filter(s => s.title.match(/^20[0-9]{2}$/)).map(s => s.title)))
  const yearPicker = availableYears.length > 1 ? <YearPicker name={`year-${ticket.id}`} years={availableYears} year={year} setYear={setYear} /> : null

  const calendarPopUp = generalTicket ? <CalendarTicketPopUp ticket={generalTicket} qty={generalQty} setQty={setGeneralQty} /> : null
  const claendarPopUpTrigger = generalTicket ? <CalendarTicketPopUp ticket={generalTicket} trigger={'Select Tickets >'} qty={generalQty} setQty={setGeneralQty} /> : null

  const yearPickerSection = (): ReactNode => {
    return yearPicker ?
      (
        <div className="year-picker-section">
          {yearPicker}
        </div>
      ) : calendarPopUp ?
        (
          <div className={styles.dateWrap}>
            <span>Select a date</span> {calendarPopUp}
          </div>
        ) : null;
  }

  async function addToCartButton() {
    setLoading(true)

    const items: Array<AddToCartTicketType> = []

    if (adultTicket) items.push({ ticketId: adultTicket.ticket_id, qty: adultQty })
    if (childTicket) items.push({ ticketId: childTicket.ticket_id, qty: childQty })
    if (parkExtrasAdultTicket) items.push({ ticketId: parkExtrasAdultTicket.ticket_id, qty: adultQty })
    if (parkExtrasChildTicket) items.push({ ticketId: parkExtrasChildTicket.ticket_id, qty: childQty })
    if (parkExtrasInfantTicket) items.push({ ticketId: parkExtrasInfantTicket.ticket_id, qty: infantQty })
    if (generalTicket) items.push({ ticketId: generalTicket.product_id, qty: generalQty })
    if (items.length) await addToCart(items)
    setGeneralQty(0)
    setAdultQty(0)
    setChildQty(0)
    setInfantQty(0)

    setLoading(false)
  }


  return (
    <div>
      <section className={styles.section}>
        {!calendarPopUp ?
          <>
            <div className="year-picker-section">
              {yearPickerSection()}
            </div>
          {generalDropDown}
          {adultDropDown}
          {childDropDown}
          {parkExtrasAdultDropDown}
          {parkExtrasChildDropDown}
          {parkExtrasInfantDropDown}
          <Button
            loading={loading}
            disabled={loading || (adultQty < 1 && childQty < 1 && generalQty < 1)}
            className={styles.btn}
            onClick={calendarPopUp ? () => { calendarPopUp } : addToCartButton}
          >
            Add to cart
          </Button>
          </>
          :
          <>
            {generalTicket &&
            <div className={styles.ticket}>
              <span className={styles.price}>{name === 'Discovery Cove' && 'from'} £{generalTicket.price_from}</span>
            </div>
            }
            {claendarPopUpTrigger}
          </>
        }
      </section>
    </div>
  )
}
